import {StyleSheet, css} from 'aphrodite';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import * as OnboardingActions from 'actions/OnboardingActions';
import GapSequenceWrapper from 'analyses/alignment/GapSequenceWrapper';
import {Alignment} from 'analyses/Analyses';
import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {SequenceVersion} from 'components/Sequence';
import {DNAAlignmentKind} from 'constants/AlignmentConstants';
import OnboardingConstants from 'constants/OnboardingConstants';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import PolymerAlignmentTabView, {ScrollAnimatorData} from 'molbio/maps/alignment/PolymerAlignmentTabView';
import {
  SequenceAlignmentRowsView,
  InfiniteSequenceAlignmentRowsView,
} from 'molbio/maps/alignment/SequenceAlignmentRowsView';
import {SequenceAlignmentRowView} from 'molbio/maps/alignment/SequenceAlignmentRowView';
import AlignmentSummaryMapView from 'molbio/maps/alignment/summary/AlignmentSummaryMapView';
import {AlignmentMapState, AlignmentMapStates, SummaryMapSingleState} from 'molbio/maps/MapStates';
import Type from 'molbio/maps/Type';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import notifications from 'notifications';
import addTestAttrsToDOMElement from 'test/lib/cypress/addTestAttrsToDOMElement';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import * as LoadingScreen from 'ui-library/LoadingScreen';

const alignmentBannerDismissedState = makeUseGlobalClientState(false);

function OutOfSyncBanner({
  alignmentType,
  alignmentVersion,
}: {
  alignmentType: DNAAlignmentKind;
  alignmentVersion: SequenceVersion | undefined;
}): JSX.Element {
  return (
    <span>
      Out of sync. Edits were made to the {alignmentType} sequence while the alignment was closed. Please
      realign
      {alignmentVersion != null && (
        <>
          {' '}
          or{' '}
          <a
            onClick={() => {
              alignmentVersion.trigger('open', alignmentVersion);
            }}
          >
            revert to <i>{alignmentVersion.get('name')}</i>
          </a>
        </>
      )}
      .
    </span>
  );
}

const styles = StyleSheet.create({
  bannerMargin: {
    margin: '8px',
  },
});
export default class SequenceAlignmentTabView extends PolymerAlignmentTabView<
  SummaryMapSingleState,
  SequenceAlignmentTabState
> {
  undoInProgress!: boolean;
  mapstates!: AlignmentMapStates;
  mapstateSequences!: Array<GapSequenceWrapper>;
  rowsView!: InfiniteSequenceAlignmentRowsView | SequenceAlignmentRowsView;

  initialize(options: Backbone.ViewOptions<SequenceAlignmentTabState>): void {
    super.initialize(options);
    this.mapstates = this.model.get('mapstates');
    this.undoInProgress = false;
    OnboardingActions.completeOnboardingTask(
      OnboardingConstants.MODULES.SEQUENCE,
      OnboardingConstants.VIEW_ALIGNMENT
    );
  }

  getDataTestAttrs(): DataTestAttrs {
    return {component: 'SequenceAlignmentTab', element: 'root'};
  }

  getType(): Type {
    return this.model.get('dnaType');
  }

  setType(typeObj: Type): void {
    this.getType().copy(typeObj);
  }

  getPolymerLength(): number {
    return this.model.get('analysis')!.getMasterSequence().length();
  }

  addPolymersForSearch(): void {
    this.mapstateSequences = this.model.get('mapstates').map((mapstate) => mapstate.get('sequence'));
    SearchDNAData.get().addSequences(this.mapstateSequences, 10);
  }

  removePolymersForSearch(): void {
    if (!this.mapstateSequences) {
      return;
    }
    SearchDNAData.get().removeSequence(...this.mapstateSequences);
  }

  setMapDimensions(...args: [boolean] | []): void {
    // TODO: Are these used?
    for (const mapstate of this.mapstates.models) {
      mapstate.set({width: this.model.get('bufferViewLayout').get('width')});
      mapstate.set({height: this.model.get('bufferViewLayout').get('height')});
    }
    super.setMapDimensions(...args);
  }

  /**
   * Set the sequence id of the template sequence to the current sequence, so realigning
   *  knows what to do this is only a problem because we can clone sequences/analyses.
   */
  linkMasterSequenceToActualSequence(): void {
    if (this.model.get('analysis')!.get('alignmentType') === 'template') {
      this.model
        .get('analysis')!
        .getMasterSequence()
        .set({
          id: this.model.get('sequence').get('_sequence').get('id'),
        });
    }
  }

  handleAlignmentSort(): void {
    this.rowsView.handleAlignmentSort();
  }

  handleAlignmentRemoval(): void {
    this.save();
    // The AlignmentView that was destroyed, will remove itself.
  }

  handleAlignmentUpdate(): void {
    this.save();
    this.setMapDimensions();
    this.showRange(true);
    this.summaryMapView.showFullSequence();
  }

  registerListeners(): void {
    super.registerListeners();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('analysis')!.get('alignments'), 'remove', this.handleAlignmentRemoval);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('analysis')!.get('alignments'), 'update', this.handleAlignmentUpdate);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('mapstates'), 'sort', this.handleAlignmentSort);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('analysis')!.get('alignments'), 'save', this.save);
    // Ensure views are resized if a summary slice changes size.
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model.get('summaryState'), 'change:sliceHeight', this.resizeContainer);

    // Re-render warning banners when there are changes that can effect the sync status.
    this.listenTo(this.model.get('sequence').get('versions'), 'add remove reset', () => {
      this.maybeRenderBanners();
    });
    this.listenTo(this.model.get('analysis')!, 'change:versionId', () => {
      this.maybeRenderBanners();
    });
    this.listenTo(this.model.get('range')!, 'rangeChange', () => {
      this.mapstates.forEach((mapstate: AlignmentMapState) => {
        mapstate.adjustLayoutsForRange(this.model.get('range')!);
      });
    });

    // Handle jump (for search?)
    for (const mapstate of this.model.get('mapstates').models) {
      const sequence = mapstate.get('sequence');
      const ref = this;
      this.listenTo(sequence, 'jump', (target: number, cid: string, endTarget: number | null = null) => {
        if (endTarget == null) {
          endTarget = target + 1;
        }
        const start = this.model.get('range')!.start();
        const end = this.model.get('range')!.end();

        // Scroll horizontally if not already in view
        if (!(start <= target && endTarget <= end)) {
          const span = end - start;
          $(this.scrollAnimator)
            .stop()
            .animate(
              {base: Math.floor((target + endTarget) / 2)},
              {
                duration: Math.max(100, Math.min(300, Math.abs(target - start) * 5)),
                step() {
                  // For some reason, TypeScript thinks that `this` binds to something else
                  ref.model.get('range')!.panStart(Math.floor((this as ScrollAnimatorData).base - span / 2));
                  ref.showRange(false);
                },
                done() {
                  // For some reason, TypeScript thinks that `this` binds to something else
                  ref.model.get('range')!.panStart(Math.floor((this as ScrollAnimatorData).base - span / 2));
                  ref.showRange(false);
                },
              }
            );
        }

        // Scroll vertically to the sequence that triggered the jump, if the sequence wasn't
        // the master sequence, which is always in view
        const index = this.mapstates.indexOf(mapstate);
        if (index > 0) {
          // Adjust index to account for rowsView only rendering the non-master sequences
          this.rowsView.jumpToIndex(index - 1);
        }
      });
    }
  }

  _saveOrder(cids: Array<string>): void {
    const alignments = this.model.get('analysis')!.get('alignments');
    const topId = $('.fixed-top').find('.alignment-row').attr('data-id');
    cids.unshift(topId);
    alignments.comparator = (row: Alignment) => cids.indexOf(row.cid);
    // eslint-disable-next-line no-restricted-syntax
    alignments.sort();
    analytics.track('alignment', 'reorder_alignments', {
      name: this.model.get('analysis')!.get('name'),
      type: this.model.get('analysis')!.get('alignmentType'),
    });
    this.save();
  }

  async save(options: {triggeredByUndo?: boolean} = {}): Promise<void> {
    await super.save();
    // Without this check all undo operations after the first would just reload the current state
    if (BenchlingConfig.MOLBIO_ENABLE_ALIGNMENT_UNDO && !options.triggeredByUndo) {
      this.model.addToHistory();
    }
  }

  async undo(): Promise<void> {
    if (!BenchlingConfig.MOLBIO_ENABLE_ALIGNMENT_UNDO || !this.model.canUndo()) {
      return;
    }

    if (this.undoInProgress) {
      notifications.warn('Cannot undo while previous operation is in progress. Try again in a moment.');
    } else {
      this.undoInProgress = true;
      // The defer here is to ensure the loading screen is flushed to the DOM synchronously
      _.defer(() => {
        LoadingScreen.show('Reverting...');
      });
      try {
        const [rowIndex, residueIndex] = await this.model.undo();
        if (rowIndex != null && residueIndex != null) {
          await this.save({triggeredByUndo: true});
          if (this.model.get('analysis')!.isReallyBig()) {
            (this.rowsView as InfiniteSequenceAlignmentRowsView).rerenderRows();
          }
          this.model.jumpToLocation({rowIndex, residueIndex});
        }
      } finally {
        LoadingScreen.hide();
        this.undoInProgress = false;
      }
    }
  }

  makeSummaryMapView(): AlignmentSummaryMapView<SummaryMapSingleState> {
    return new AlignmentSummaryMapView({
      model: this.model.get('summaryState'),
      tabstate: this.model,
      isCollapsed: this.shouldCollapseMiniMap(this.model),
    });
  }

  getBanners(): JSX.Element {
    // should be kept in sync with benchling/pybio/alignment.py
    const ALIGNMENT_SEQUENCE_CUMULATIVE_BASE_LIMIT = 5e6;
    const analysis = this.model.get('analysis')!;
    const cumulativeBases: number = analysis
      .get('alignments')
      // @ts-expect-error: Missing arg
      .collect()
      .reduce((acc: number, alignment: Alignment): number => {
        const gapSequence = alignment.get('sequence');
        return acc + gapSequence.gapToReal(gapSequence.length());
      }, 0) as number;

    const inlineBannerStackMessages: Array<InlineBannerProps> = [];
    const versionId = analysis.get('versionId');
    if (cumulativeBases > ALIGNMENT_SEQUENCE_CUMULATIVE_BASE_LIMIT) {
      const productLimitWarningKey = `alignment-limit-${analysis.id}-${versionId}`;
      if (!alignmentBannerDismissedState.getState(productLimitWarningKey)) {
        inlineBannerStackMessages.push({
          bannerType: InlineBannerType.WARNING,
          text: `This alignment exceeds Benchling’s limit of ${ALIGNMENT_SEQUENCE_CUMULATIVE_BASE_LIMIT.toLocaleString()} bases across all sequences in the alignment. Realignments that increase its size may fail.`,
          onDismiss: () => {
            alignmentBannerDismissedState.updateState(productLimitWarningKey, true);
            this.maybeRenderBanners();
          },
        });
      }
    }
    if (!this.model.inSync()) {
      const outOfSyncWarningKey = `alignment-sync-${analysis.id}-${versionId}`;
      if (!alignmentBannerDismissedState.getState(outOfSyncWarningKey)) {
        inlineBannerStackMessages.push({
          bannerType: InlineBannerType.WARNING,
          text: (
            <OutOfSyncBanner
              alignmentType={analysis.get('alignmentType')}
              alignmentVersion={this.model.get('sequence').get('versions').get(versionId)}
            />
          ),
          onDismiss: () => {
            alignmentBannerDismissedState.updateState(outOfSyncWarningKey, true);
            this.maybeRenderBanners();
          },
        });
      }
    }
    if (inlineBannerStackMessages.length > 0) {
      return (
        <div className={css(styles.bannerMargin)}>
          <InlineBannerStack messages={inlineBannerStackMessages} />
        </div>
      );
    } else {
      return <></>;
    }
  }

  makeRowsView(): InfiniteSequenceAlignmentRowsView | SequenceAlignmentRowsView {
    const isReallyBig = this.model.get('analysis')!.isReallyBig();
    const rowsView = isReallyBig
      ? new InfiniteSequenceAlignmentRowsView({model: this.model, tabView: this})
      : new SequenceAlignmentRowsView({
          model: this.model,
          // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
          saveOrderHandler: (cids: Array<string>): void => this._saveOrder(cids),
          tabView: this,
        });
    if (isReallyBig) {
      this.model.set('scrollData', (rowsView as InfiniteSequenceAlignmentRowsView).scrollData);
    }
    return rowsView;
  }

  makeTopAlignmentRowView(): SequenceAlignmentRowView {
    const tabstate = this.model;
    const topMapstate = tabstate
      .get('mapstates')
      .find(
        (mapstate) => mapstate.get('alignment').get('type') === tabstate.get('analysis')!.get('alignmentType')
      );
    const view = new SequenceAlignmentRowView({model: topMapstate, tabstate, tabView: this});

    // Store data to help with sort.
    $(view.el).attr('data-id', topMapstate.get('alignment').cid);
    $(view.el).data('data-view', view);
    addTestAttrsToDOMElement(view.el, {component: 'AlignmentRow', element: 'root', key: 'referenceSequence'});

    return view;
  }
}
SequenceAlignmentTabView.initClass();
